import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, resource, ResourceRef, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { useI18n } from '@sona/angular-util-common';
import { ScanRequestMeter, TargetId } from '@sona/shared-model-common';
import { Report } from '@sona/shared-reports-models';
import {
    AuditTypeId,
    IAuditType, ICreateSingleRescanRequestDto,
    IScan,
    ScanCategory,
    ScanId,
    ScanRequestId
} from '@sona/shared-scan-model-common';
import { Paginated } from 'nestjs-paginate';
import { toast } from 'ngx-sonner';
import { injectParams } from 'ngxtension/inject-params';
import { filter, firstValueFrom, switchMap } from 'rxjs';

import { Issue } from '../models/issue';
import { Page } from '../models/page';
import { PaginationParams } from '../models/pagination-params';
import { tableQueryParamsTransformer, TableSate } from '../utils/table-utils';

import { DeepScanApi } from './deep-scan-api';
import { LatestDeepScanApi } from './latest-deep-scan-api';



@Injectable()
export class ReportFacade {
    private readonly _route = inject(ActivatedRoute);
    private readonly _http = inject(HttpClient);
    private readonly _deepScanApi = inject(DeepScanApi);
    private readonly _latestDeepScanApi = inject(LatestDeepScanApi);
    private readonly _i18n = useI18n();

    readonly targetId = injectParams('id') as Signal<TargetId | null>;
    readonly category = signal<ScanCategory>(this._route.snapshot.paramMap.get('category') as ScanCategory ?? ScanCategory.ACCESSIBILITY);
    readonly scanAborted = signal<boolean>(false);
    readonly latestReportMetrics = resource({
        request: () => ({ targetId: this.targetId() }),
        loader: async ({ request }) => {
            try {
                return await firstValueFrom(this._latestDeepScanApi.getLatestReportMetrics(request.targetId));
            } catch (e) {
                return null;
            }
        }
    });
    readonly latestReportChanges = toSignal(toObservable(this.targetId)
        .pipe(
            switchMap((targetId) => this._latestDeepScanApi.getLatestReportChanges(targetId))
        ));

    readonly scanRequestId = computed(() => this.latestReportMetrics.value()?.id);

    readonly latestReportRescanChanges = toSignal(toObservable(this.scanRequestId)
        .pipe(
            filter(Boolean),
            switchMap((scanRequestId) => this._deepScanApi.getReportRescanChanges(scanRequestId))
        ), {
        initialValue: undefined
    });

    readonly metricsExist: ResourceRef<{metricsExist: boolean}> = resource({
        request: () => ({ targetId: this.targetId() }),
        loader: async ({ request }) => {
            return await firstValueFrom(
                this._latestDeepScanApi.checkMetricsExist(request.targetId)
            );
        }
    });

    readonly latestCategoryReport: ResourceRef<Report> = resource({
        request: () => ({ scanRequestId: this.scanRequestId(), category: this.category() }),
        loader: ({ request }) => firstValueFrom(this._deepScanApi.getCategoryReport(request.scanRequestId, request.category))
    });

    /* readonly siteMetrics = resource({
        request: () => ({ scanRequestId: this.scanRequestId(), category: this.category() }),
        loader: ({ request }) => {
            if (!request.scanRequestId) {
                return Promise.reject('`scanRequestId` not set');
            } else if (!request.category) {
                return Promise.reject('`category` not set');
            }

            return firstValueFrom(this._httpClient.get<PageMetrics>(`/api/reports/deep/${request.scanRequestId}/pages/metrics/${request.category}`));
        }
    }); */

    readonly issueTabTableState = signal<TableSate>({
        search: null,
        pagination: {
            pageIndex: 0,
            pageSize: 10
        },
        sorting: [ { id: 'issueCount', desc: true } ]
    });
    readonly issuesQueryParamsTransformer = tableQueryParamsTransformer();
    readonly issuesTabQueryParams = computed(() => this.issuesQueryParamsTransformer.stringify(this.issueTabTableState()));

    readonly pagesTabTableState = signal<TableSate>({
        search: null,
        pagination: {
            pageIndex: 0,
            pageSize: 10
        },
        sorting: [ { id: 'issueCount', desc: true } ]
    });
    readonly pagesQueryParamsTransformer = tableQueryParamsTransformer();
    readonly pagesTabQueryParams = computed(() => this.pagesQueryParamsTransformer.stringify(this.pagesTabTableState()));

    readonly affectedPagesTabTableState = signal<TableSate>({
        search: null,
        pagination: {
            pageIndex: 0,
            pageSize: 10
        },
        sorting: [ /* { id: 'audits.issues', desc: true } */ ]
    });

    async rescan(meter: ScanRequestMeter) {
        // Reset the aborted flag when starting a new scan
        this.scanAborted.set(false);
        await this._latestDeepScanApi.createDeepScan(this.targetId(), meter);
    }

    async abortScan(id: ScanRequestId) {
        try {
            await firstValueFrom(this._deepScanApi.abort(id));

            // Set the aborted flag to prevent showing success message
            this.scanAborted.set(true);
            
            this.latestReportMetrics.reload();
            this.latestCategoryReport.reload();
            toast.success(this._i18n().t('REPORTS.API.ABORT.SUCCESS'));
        } catch (error) {
            console.error('Failed to abort DeepScan:', error);
            toast.error(this._i18n().t('REPORTS.API.ABORT.ERROR'));
        }
    }



    getIssues(params: () => PaginationParams): ResourceRef<Paginated<Issue>> {
        return resource({
            request: () => ({
                params: params(),
                scanRequestId: this.scanRequestId(),
                category: this.category()
            }),
            loader: ({ request }) => firstValueFrom(this._deepScanApi.getIssues(request.scanRequestId, request.category, request.params))
        });
    }

    getPages(params: () => PaginationParams): ResourceRef<Paginated<Page>> {
        return resource({
            request: () => ({
                params: params(),
                scanRequestId: this.scanRequestId(),
                category: this.category()
            }),
            loader: ({ request }) => firstValueFrom(this._deepScanApi.getPages(request.scanRequestId, request.category, request.params))
        });
    }

    getAffectedPages(auditTypeId: Signal<AuditTypeId>, params: () => PaginationParams): ResourceRef<Paginated<IScan>> {
        return resource({
            request: () => ({
                auditTypeId: auditTypeId(),
                params: params(),
                scanRequestId: this.scanRequestId(),
                category: this.category()
            }),
            loader: ({ request }) => firstValueFrom(this._deepScanApi.getAffectedPages(request.scanRequestId, request.category, request.auditTypeId, request.params))
        });
    }

    getIssue(auditTypeId: Signal<AuditTypeId>): ResourceRef<IAuditType> {
        return resource({
            request: () => ({ auditTypeId: auditTypeId() }),
            loader: ({ request }) => {
                return firstValueFrom(this._http.get<IAuditType>(`/api/scan/audit-types/${request.auditTypeId}`));
            }
        });
    }

    getScan(scanId: Signal<ScanId>): ResourceRef<IScan> {
        return resource({
            request: () => ({ scanId: scanId() }),
            loader: ({ request }) => {
                return firstValueFrom(this._http.get<IScan>(`/api/scan/${request.scanId}`));
            }
        });
    }

    rescanSinglePage(deepScanRequestId: ScanRequestId, body: ICreateSingleRescanRequestDto) {
        return firstValueFrom(this._deepScanApi.rescanPage(deepScanRequestId, body));
    }

    async getCategoryReportFile() {
        return firstValueFrom(this._deepScanApi.getCategoryReportFile(this.scanRequestId(), this.category()));
    }
}
